import type { ReactNode } from "react";
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Paper, Typography,
} from "@mui/material";
import { PortalApiError } from "../api";
import { errorMessage } from "../helpers";

/**
 * Loading, empty and failed, as in the Console's DataState — which is not
 * reused because it imports the Console's API client.
 */

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 6, justifyContent: "center" }}>
      <CircularProgress size={22} />
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  );
}

export function Empty({ title, hint, action }: {
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ py: 6, px: 3, textAlign: "center" }}>
      <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mx: "auto", maxWidth: 520 }}>
          {hint}
        </Typography>
      ) : null}
      {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
    </Paper>
  );
}

export function Failed({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const status = error instanceof PortalApiError ? error.status : null;
  const forbidden = status === 403;
  return (
    <Alert
      severity={forbidden ? "info" : "error"}
      action={onRetry && !forbidden ? (
        <Button size="small" color="inherit" onClick={onRetry}>Retry</Button>
      ) : undefined}
    >
      <AlertTitle>
        {forbidden ? "You do not have access to this"
          : status === 404 ? "Not found"
          : "That did not work"}
      </AlertTitle>
      {errorMessage(error)}
    </Alert>
  );
}

/** A form's own failure, inline, in the server's words. */
export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  return <Alert severity="error">{errorMessage(error)}</Alert>;
}

export function Async<T>({ query, children }: {
  query: { isLoading: boolean; error: unknown; data: T | undefined; refetch: () => unknown };
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) return <Loading />;
  if (query.error) return <Failed error={query.error} onRetry={() => void query.refetch()} />;
  if (query.data === undefined) return null;
  return <>{children(query.data)}</>;
}
